// Premium DARK "PIE Pathway Report" PDF generator (per the PIE Report Spec).
// Builds each page as a fixed 816x1056 dark DOM node, captures with html2canvas
// (dark preserved), and assembles a Letter PDF. One section per page, capped so
// content never overflows a page — nothing is cut across pages.
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { computeProjection, fmtMoney } from './futureOutlook';

const T = {
  void: '#01040E', deep: '#04101F', panel: '#08203A', blue: '#0089FB', cyan: '#17CCF4',
  ice: '#EAF3FB', steel: '#8FB0CE', dsteel: '#5C7C9E', green: '#2FE0A8', hair: 'rgba(0,137,251,0.30)',
};
const SERIF = 'Georgia, "Century Schoolbook", serif';
const SANS = 'Arial, Helvetica, sans-serif';
const PW = 816, PH = 1056;

const el = (tag, style = {}, text) => { const e = document.createElement(tag); Object.assign(e.style, style); if (text != null) e.textContent = text; return e; };

function loadLogo() {
  return new Promise((res) => {
    const img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = () => res(img); img.onerror = () => res(null); img.src = '/fp-logo.png';
  });
}

function makePage(logo, { footerCenter = 'Personal Pathway Report', pageNum } = {}) {
  const page = el('div', {
    width: PW + 'px', height: PH + 'px', background: T.void, position: 'relative',
    fontFamily: SANS, color: T.ice, boxSizing: 'border-box', padding: '56px 56px 64px 56px', overflow: 'hidden',
  });
  page.className = 'pie-page';
  if (logo && pageNum) { // interior watermark
    const w = el('img', { position: 'absolute', top: '28px', right: '40px', width: '30px', height: '30px', opacity: '0.85' });
    w.src = logo.src; page.appendChild(w);
  }
  const body = el('div', { position: 'relative', height: '100%' });
  page.appendChild(body);
  if (pageNum) {
    const f = el('div', { position: 'absolute', left: '56px', right: '56px', bottom: '30px' });
    f.appendChild(el('div', { borderTop: `1px solid ${T.hair}`, marginBottom: '8px' }));
    const row = el('div', { display: 'flex', justifyContent: 'space-between', fontSize: '11px', letterSpacing: '0.5px', color: T.dsteel });
    row.appendChild(el('span', {}, 'AGENTIC FUTUREPATH AI · PERSONAL PATHWAY REPORT'));
    row.appendChild(el('span', {}, footerCenter));
    row.appendChild(el('span', { color: T.blue }, String(pageNum)));
    f.appendChild(row); page.appendChild(f);
  }
  return { page, body };
}

const eyebrow = (t) => el('div', { color: T.cyan, fontSize: '12px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '8px' }, t);
const title = (t) => el('div', { fontFamily: SERIF, fontWeight: '700', fontSize: '32px', color: T.ice, lineHeight: '1.15', marginBottom: '6px' }, t);
const subtitle = (t) => el('div', { color: T.steel, fontSize: '14px', marginBottom: '26px' }, t);

function sectionHead(body, num, ttl, sub) {
  body.appendChild(eyebrow(`SECTION ${String(num).padStart(2, '0')}`));
  body.appendChild(title(ttl));
  if (sub) body.appendChild(subtitle(sub));
}

const card = (children = [], extra = {}) => {
  const c = el('div', {
    background: `linear-gradient(160deg, ${T.panel}, ${T.deep})`, border: `1px solid ${T.hair}`,
    borderRadius: '14px', padding: '20px 22px', marginBottom: '14px', ...extra,
  });
  children.forEach((ch) => c.appendChild(ch));
  return c;
};

function statGrid(items) {
  const g = el('div', { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '14px', marginBottom: '18px' });
  items.slice(0, 6).forEach((it) => {
    const c = card([], { textAlign: 'center', padding: '20px 14px', marginBottom: '0' });
    c.appendChild(el('div', { fontFamily: SERIF, fontWeight: '700', fontSize: '40px', color: T.cyan, lineHeight: '1' }, it.value));
    c.appendChild(el('div', { color: T.steel, fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', marginTop: '8px' }, it.label));
    g.appendChild(c);
  });
  return g;
}

function para(t, color = T.ice) {
  return el('p', { fontSize: '14.5px', lineHeight: '1.6', color, margin: '0 0 12px 0' }, t);
}

function barRow(label, pct, right) {
  const wrap = el('div', { marginBottom: '12px' });
  const top = el('div', { display: 'flex', justifyContent: 'space-between', marginBottom: '5px' });
  top.appendChild(el('span', { color: T.ice, fontSize: '13.5px' }, label));
  top.appendChild(el('span', { color: T.blue, fontSize: '13.5px', fontWeight: '700' }, right));
  wrap.appendChild(top);
  const track = el('div', { height: '10px', background: T.deep, borderRadius: '999px', overflow: 'hidden' });
  track.appendChild(el('div', { height: '100%', width: Math.max(3, Math.min(100, pct)) + '%', background: T.blue, borderRadius: '999px' }));
  wrap.appendChild(track);
  return wrap;
}

function table(headers, rows) {
  const t = el('table', { width: '100%', borderCollapse: 'collapse', fontSize: '13px' });
  const thead = el('tr', {});
  headers.forEach((h, i) => thead.appendChild(el('th', {
    textAlign: i === 0 ? 'left' : 'right', color: T.cyan, fontSize: '11px', letterSpacing: '1px',
    textTransform: 'uppercase', padding: '10px 10px', background: T.deep, fontWeight: '700',
  }, h)));
  t.appendChild(thead);
  rows.forEach((r, ri) => {
    const tr = el('tr', { background: ri % 2 ? 'transparent' : 'rgba(4,16,31,0.6)' });
    r.forEach((cell, i) => tr.appendChild(el('td', {
      textAlign: i === 0 ? 'left' : 'right', color: i === 0 ? T.ice : T.blue, padding: '9px 10px',
      borderBottom: `1px solid ${T.hair}`, fontWeight: i === 0 ? '400' : '600',
    }, String(cell))));
    t.appendChild(tr);
  });
  return t;
}

function pullQuote(t) {
  return el('div', {
    background: T.panel, borderLeft: `3px solid ${T.cyan}`, borderRadius: '10px', padding: '18px 22px',
    fontFamily: SERIF, fontStyle: 'italic', fontSize: '17px', color: T.ice, lineHeight: '1.5', marginTop: '8px',
  }, t);
}

const clip = (s, n) => (s && s.length > n ? s.slice(0, n - 1).trim() + '…' : s);

export async function buildReportPages(data, selectedKeys) {
  const logo = await loadLogo();
  const N = data.narratives || {};
  const profile = data.profile || {};
  const name = profile.name || 'Student';
  const genDate = new Date(data.generated_at || Date.now()).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const has = (k) => selectedKeys.includes(k) && (Array.isArray(data[k]) ? data[k].length : (k === 'outlook' ? !!data.outlook?.scenario : !!data[k]));
  const proj = data.outlook?.scenario ? computeProjection(data.outlook.scenario) : null;

  // ── Assemble section builders (only completed) in spec order ──
  const builders = [];
  if (has('personality')) builders.push({ ttl: 'Who You Are', sub: 'Your strengths, and how you\'re wired.', build: (b) => {
    const p = data.personality || {};
    const type = profile.personality_type || (typeof p.personality_type === 'object' ? p.personality_type?.name : p.personality_type);
    if (type) b.appendChild(card([el('div', { color: T.cyan, fontWeight: '700', fontSize: '18px' }, type), para(clip(p.description || p.personality_type?.description || N.personality, 320), T.steel)]));
    else if (N.personality) b.appendChild(para(clip(N.personality, 420), T.steel));
    const s = (p.strengths || p.personality_type?.strengths || []).slice(0, 4);
    const g = (p.growth_areas || p.personality_type?.growth_areas || []).slice(0, 4);
    const two = el('div', { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' });
    if (s.length) { const c = card([el('div', { color: T.green, fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }, 'Strengths')]); s.forEach((x) => c.appendChild(el('div', { color: T.ice, fontSize: '13px', marginBottom: '4px' }, '• ' + x))); two.appendChild(c); }
    if (g.length) { const c = card([el('div', { color: T.cyan, fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }, 'Growth Areas')]); g.forEach((x) => c.appendChild(el('div', { color: T.ice, fontSize: '13px', marginBottom: '4px' }, '• ' + x))); two.appendChild(c); }
    if (s.length || g.length) b.appendChild(two);
  } });

  if (has('careers')) builders.push({ ttl: 'What You\'re Drawn To', sub: 'The careers you explored and how well they fit.', build: (b) => {
    if (N.careers) b.appendChild(para(clip(N.careers, 360), T.steel));
    const c = card();
    (data.careers || []).slice(0, 7).forEach((x, i) => {
      const nm = typeof x === 'string' ? x : (x.title || x.name || x.career || 'Career');
      const score = (typeof x === 'object' && (x.match_score || x.match)) || (92 - i * 6);
      c.appendChild(barRow(nm, score, Math.round(score) + '%'));
    });
    b.appendChild(c);
  } });

  if (has('pathways')) builders.push({ ttl: 'Pathways Compared', sub: 'The routes you weighed to reach your goal.', build: (b) => {
    if (N.pathways) b.appendChild(para(clip(N.pathways, 320), T.steel));
    const rows = (data.pathways || []).slice(0, 7).map((p) => [typeof p === 'string' ? p : (p.name || p.title || 'Pathway'), p.timeline || p.time_to_earn || '—', p.estimated_cost || '—', p.match_score ? Math.round(p.match_score) + '%' : (p.fit || '—')]);
    if (rows.length) b.appendChild(card([table(['Pathway', 'Time', 'Est. Cost', 'Fit'], rows)], { padding: '8px' }));
  } });

  if (has('colleges')) builders.push({ ttl: 'Schools & Programs', sub: 'The schools you\'re considering.', build: (b) => {
    if (N.colleges) b.appendChild(para(clip(N.colleges, 300), T.steel));
    const rows = (data.colleges || []).slice(0, 10).map((c) => [c.name, [c.city, c.state].filter(Boolean).join(', ') || '—', c.tuition || '—']);
    if (rows.length) b.appendChild(card([table(['School', 'Location', 'Tuition'], rows)], { padding: '8px' }));
  } });

  if (has('outlook') && proj) builders.push({ ttl: 'The Money & The Life You Want', sub: 'What your path funds over a lifetime.', build: (b) => {
    if (N.outlook) b.appendChild(para(clip(N.outlook, 300), T.steel));
    b.appendChild(statGrid([
      { value: fmtMoney(proj.summary.lifetimeEarnings), label: 'Lifetime Earnings' },
      { value: fmtMoney(proj.summary.netWorthAtRetirement), label: 'Net Worth @ Retire' },
      { value: proj.summary.financialIndependenceAge ? 'Age ' + proj.summary.financialIndependenceAge : '—', label: 'Financial Independence' },
    ]));
    const l = data.lifestyle || {};
    const needed = l.salary?.required || l.salary?.annual || data.outlook.scenario.target_income;
    const projected = data.outlook.scenario.salary_mid;
    if (needed || projected) {
      const two = el('div', { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginTop: '4px' });
      two.appendChild(card([el('div', { color: T.steel, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }, 'Income needed'), el('div', { fontFamily: SERIF, color: T.ice, fontSize: '26px', marginTop: '6px' }, fmtMoney(needed) + '/yr')]));
      two.appendChild(card([el('div', { color: T.steel, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }, 'Projected mid-career'), el('div', { fontFamily: SERIF, color: T.green, fontSize: '26px', marginTop: '6px' }, fmtMoney(projected) + '/yr')]));
      b.appendChild(two);
    }
  } });

  if (has('scholarships')) builders.push({ ttl: 'Scholarships & Funding', sub: 'Opportunities matched to you.', build: (b) => {
    if (N.scholarships) b.appendChild(para(clip(N.scholarships, 280), T.steel));
    const rows = (data.scholarships || []).slice(0, 8).map((s) => [s.name, s.amount ? fmtMoney(s.amount) : '—', s.match_score ? Math.round(s.match_score) + '%' : '—']);
    if (rows.length) b.appendChild(card([table(['Opportunity', 'Amount', 'Match'], rows)], { padding: '8px' }));
  } });

  if (has('skills')) builders.push({ ttl: 'Readiness & Skills', sub: 'Where you\'re strong and what to build next.', build: (b) => {
    const s = data.skills || {};
    if (s.ai_readiness_score != null) b.appendChild(statGrid([{ value: s.ai_readiness_score + '/100', label: 'AI-Readiness Score' }]));
    if (s.summary || N.skills) b.appendChild(para(clip(s.summary || N.skills, 340), T.steel));
    const top = (s.top_skills || []).slice(0, 6);
    if (top.length) { const c = card([el('div', { color: T.cyan, fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }, 'Top Skills')]); top.forEach((x) => c.appendChild(el('div', { color: T.ice, fontSize: '13px', marginBottom: '4px' }, '• ' + (typeof x === 'string' ? x : x.name)))); b.appendChild(c); }
  } });

  if (has('experiences')) builders.push({ ttl: 'Internships & Experiences', sub: 'Real-world opportunities you\'ve saved.', build: (b) => {
    if (N.experiences) b.appendChild(para(clip(N.experiences, 300), T.steel));
    const list = data.experiences || [];
    if (list.length) {
      const c = card();
      list.slice(0, 8).forEach((x, i) => {
        const nm = typeof x === 'string' ? x : (x.title || x.name || x.role || 'Experience');
        const meta = typeof x === 'object' ? [x.company || x.organization, x.type || x.category, x.location].filter(Boolean).join(' · ') : '';
        const r = el('div', { padding: '9px 0', borderBottom: i < Math.min(list.length, 8) - 1 ? `1px solid ${T.hair}` : 'none' });
        r.appendChild(el('div', { color: T.ice, fontSize: '14px', fontWeight: '600' }, nm));
        if (meta) r.appendChild(el('div', { color: T.steel, fontSize: '12px', marginTop: '2px' }, meta));
        c.appendChild(r);
      });
      b.appendChild(c);
    }
  } });

  if (has('financial_literacy')) builders.push({ ttl: 'Financial Literacy', sub: 'Your money-skill progress.', build: (b) => {
    const f = data.financial_literacy || {};
    if (N.financial_literacy) b.appendChild(para(clip(N.financial_literacy, 300), T.steel));
    b.appendChild(statGrid([{ value: String(f.completed_tips?.length || 0), label: 'Money Tips Completed' }, { value: String(f.lessons?.length || 0), label: 'Lessons' }]));
    const c = card(); (f.lessons || []).slice(0, 6).forEach((l) => c.appendChild(el('div', { color: T.ice, fontSize: '13.5px', marginBottom: '5px' }, '• ' + l.title))); if ((f.lessons || []).length) b.appendChild(c);
  } });

  if (has('decision_lab') && data.decision_lab) builders.push({ ttl: 'Where You Landed', sub: 'Your decision and the alternates you kept open.', build: (b) => {
    if (N.decision_lab) b.appendChild(para(clip(N.decision_lab, 340), T.steel));
    const c = card(); Object.entries(data.decision_lab).slice(0, 8).forEach(([k, v]) => { const r = el('div', { display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${T.hair}` }); r.appendChild(el('span', { color: T.steel, fontSize: '13px' }, k.replace(/_/g, ' '))); r.appendChild(el('span', { color: T.ice, fontSize: '13px', fontWeight: '600' }, typeof v === 'object' ? clip(JSON.stringify(v), 50) : String(v))); c.appendChild(r); }); b.appendChild(c);
  } });

  // ── Build pages ──
  const pages = [];
  let pageNo = 0;

  // Cover
  {
    const { page, body } = makePage(logo, {});
    body.style.display = 'flex'; body.style.flexDirection = 'column'; body.style.alignItems = 'center'; body.style.textAlign = 'center';
    const ring = el('div', { marginTop: '90px', width: '150px', height: '150px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(23,204,244,0.18), transparent 70%)', display: 'flex', alignItems: 'center', justifyContent: 'center' });
    if (logo) { const im = el('img', { width: '96px', height: '96px' }); im.src = logo.src; ring.appendChild(im); }
    body.appendChild(ring);
    body.appendChild(el('div', { color: T.cyan, fontSize: '13px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', marginTop: '34px' }, 'Personal Pathway Report'));
    body.appendChild(el('div', { fontFamily: SERIF, fontWeight: '700', fontSize: '46px', color: T.ice, marginTop: '14px' }, name));
    const oneLine = clip((N.cover_intro || '').split('. ')[0] || (profile.career_goal ? `A plan built around ${profile.career_goal}` : 'Your pathway plan'), 70);
    body.appendChild(el('div', { fontFamily: SERIF, fontStyle: 'italic', fontSize: '19px', color: T.steel, marginTop: '10px', maxWidth: '560px' }, oneLine));
    body.appendChild(el('div', { color: T.dsteel, fontSize: '13px', letterSpacing: '0.5px', marginTop: '18px' }, [genDate, profile.grade_level, [profile.city, profile.state].filter(Boolean).join(', ')].filter(Boolean).join('   ·   ')));
    if (N.cover_intro) {
      const hp = card([el('div', { color: T.ice, fontSize: '15px', lineHeight: '1.55' }, clip(N.cover_intro, 240))], { maxWidth: '560px', marginTop: '34px', textAlign: 'left' });
      body.appendChild(hp);
    }
    const foot = el('div', { position: 'absolute', bottom: '0', left: '0', right: '0', textAlign: 'center' });
    foot.appendChild(el('div', { fontFamily: SERIF, fontStyle: 'italic', color: T.steel, fontSize: '14px', marginBottom: '8px' }, 'Navigate your infinite potential.'));
    foot.appendChild(el('div', { color: T.dsteel, fontSize: '11px', letterSpacing: '1px' }, 'AGENTIC FUTUREPATH AI · AGENTICFUTUREPATH.COM · CONFIDENTIAL'));
    body.appendChild(foot);
    pages.push(page);
  }

  // Executive Summary
  {
    pageNo++; const { page, body } = makePage(logo, { footerCenter: 'Executive Summary', pageNum: pageNo + 1 });
    sectionHead(body, 1, 'Executive Summary', 'What this report shows, at a glance.');
    body.appendChild(para(clip(N.cover_intro || `${name} has been building a clear, personalized plan for life after school across the FuturePath journey.`, 520), T.ice));
    const stats = [];
    if (data.careers?.length) stats.push({ value: String(data.careers.length), label: 'Careers Explored' });
    if (data.pathways?.length) stats.push({ value: String(data.pathways.length), label: 'Pathways Compared' });
    if (data.colleges?.length) stats.push({ value: String(data.colleges.length), label: 'Schools Saved' });
    if (data.scholarships?.length) stats.push({ value: String(data.scholarships.length), label: 'Scholarships Found' });
    if (proj) stats.push({ value: fmtMoney(proj.summary.lifetimeEarnings), label: 'Projected Lifetime Earnings' });
    if (data.goals?.length) stats.push({ value: String(data.goals.length), label: 'Action Steps' });
    if (data.financial_literacy?.completed_tips?.length) stats.push({ value: String(data.financial_literacy.completed_tips.length), label: 'Money Tips Done' });
    if (stats.length) body.appendChild(statGrid(stats));
    const goal = profile.career_goal;
    if (goal) body.appendChild(pullQuote(`“My goal: ${goal}.”`));
    pages.push(page);
  }

  // TOC
  {
    pageNo++; const { page, body } = makePage(logo, { footerCenter: 'Contents', pageNum: pageNo + 1 });
    sectionHead(body, 2, 'Table of Contents', 'Everything inside this report.');
    let startPage = pageNo + 2; // first section absolute page number (1-based incl cover)
    builders.forEach((s, i) => {
      const row = el('div', { display: 'flex', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${T.hair}` });
      row.appendChild(el('span', { color: T.cyan, fontFamily: SERIF, fontSize: '16px', width: '44px' }, String(i + 1).padStart(2, '0')));
      row.appendChild(el('span', { color: T.ice, fontSize: '15px', flex: '1' }, s.ttl));
      row.appendChild(el('span', { color: T.dsteel, fontSize: '13px' }, 'p. ' + (startPage + i)));
      body.appendChild(row);
    });
    body.appendChild(el('div', { color: T.dsteel, fontSize: '11px', marginTop: '20px', fontStyle: 'italic' }, `Reflects information provided and platform analysis as of ${genDate}.`));
    pages.push(page);
  }

  // Section pages
  builders.forEach((s, i) => {
    pageNo++; const { page, body } = makePage(logo, { footerCenter: s.ttl, pageNum: pageNo + 1 });
    sectionHead(body, i + 1, s.ttl, s.sub);
    s.build(body);
    pages.push(page);
  });

  // Action plan / closing
  {
    pageNo++; const { page, body } = makePage(logo, { footerCenter: 'Your Next 30 Days', pageNum: pageNo + 1 });
    sectionHead(body, builders.length + 1, 'Your Next 30 Days', 'The steps that move your plan forward.');
    const goals = (data.goals || []).slice(0, 8);
    if (goals.length) {
      const c = card();
      goals.forEach((g, gi) => {
        const r = el('div', { display: 'flex', gap: '12px', padding: '9px 0', borderBottom: gi < goals.length - 1 ? `1px solid ${T.hair}` : 'none' });
        r.appendChild(el('span', { color: T.cyan, fontFamily: SERIF, fontWeight: '700' }, String(gi + 1)));
        r.appendChild(el('span', { color: T.ice, fontSize: '14px', flex: '1' }, g.title || g.goal || 'Action step'));
        r.appendChild(el('span', { color: T.dsteel, fontSize: '12px' }, g.status || (g.completed ? 'Done' : 'To do')));
        c.appendChild(r);
      });
      body.appendChild(c);
    } else {
      body.appendChild(para('Set your first goals in the app to populate your 30-day action plan.', T.steel));
    }
    body.appendChild(pullQuote('Every choice you make now is a step toward the life you\'re designing.'));
    body.appendChild(el('div', { color: T.dsteel, fontSize: '11px', marginTop: '18px', lineHeight: '1.5' }, 'Methodology: This report reflects information you provided and FuturePath platform analysis. Projections are estimates for exploration and are not financial advice.'));
    pages.push(page);
  }

  // ── Return the assembled page nodes (detached) for preview or PDF render ──
  return { pages, name };
}

// Render an array of (detached or attached) page nodes into a downloaded PDF.
// Pages are temporarily moved offscreen at full 816px scale so capture fidelity
// is never affected by any preview zoom/scale, then restored to their origin.
export async function renderPagesToPDF(pages, name) {
  const origin = pages.map((p) => ({ parent: p.parentNode, next: p.nextSibling }));
  const wrap = el('div', { position: 'fixed', left: '-10000px', top: '0', width: PW + 'px', background: T.void });
  pages.forEach((p) => wrap.appendChild(p));
  document.body.appendChild(wrap);

  const pdf = new jsPDF('p', 'pt', 'letter');
  const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight();
  try {
    for (let i = 0; i < pages.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      const canvas = await html2canvas(pages[i], { scale: 2, backgroundColor: T.void, useCORS: true, logging: false, width: PW, height: PH });
      const img = canvas.toDataURL('image/jpeg', 0.94);
      if (i > 0) pdf.addPage();
      pdf.addImage(img, 'JPEG', 0, 0, pw, ph);
    }
    pdf.save(`${(name || 'Student').split(' ')[0]}-FuturePath-Pathway-Report.pdf`);
  } finally {
    document.body.removeChild(wrap);
    // Restore nodes to wherever they came from (e.g. the live preview modal)
    origin.forEach(({ parent, next }, i) => { if (parent) parent.insertBefore(pages[i], next); });
  }
}

export async function generateReportPDF(data, selectedKeys) {
  const { pages, name } = await buildReportPages(data, selectedKeys);
  await renderPagesToPDF(pages, name);
}
